/**
 * ProfilePage — full profile page displaying user info, notification preferences,
 * and connected accounts sections.
 *
 * Route: /profile (accessible from header profile button)
 * Keyboard accessible with logical tab order through sections.
 *
 * Requirements: 11.1, 11.2, 11.7, 11.8
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { NotificationPreferencesSection } from '../components/NotificationPreferencesSection';
import { MyCardsSection } from '../components/profile/MyCardsSection';
import { McpKeysSection } from '../components/profile/McpKeysSection';
import { MyWatchesSection } from '../components/profile/MyWatchesSection';
import * as profileApi from '../api/user-profile';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ProfileState {
  readonly profile: profileApi.UserProfileData | null;
  readonly isLoading: boolean;
  readonly error: string | null;
  readonly updateError: string | null;
}

// ---------------------------------------------------------------------------
// Avatar with initials fallback
// ---------------------------------------------------------------------------

function UserAvatar({ displayName, avatarUrl }: { displayName: string; avatarUrl: string | null }): React.ReactElement {
  const initials = displayName
    .split(' ')
    .map((part) => part.charAt(0))
    .slice(0, 2)
    .join('')
    .toUpperCase();

  if (avatarUrl) {
    return (
      <img
        src={avatarUrl}
        alt={`${displayName} avatar`}
        className="h-16 w-16 rounded-full object-cover"
        width="64"
        height="64"
      />
    );
  }

  return (
    <div
      className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-primary text-xl font-semibold"
      aria-label={`${displayName} avatar`}
      role="img"
    >
      {initials || '?'}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ProfilePage
// ---------------------------------------------------------------------------

export function ProfilePage(): React.ReactElement {
  const [state, setState] = useState<ProfileState>({
    profile: null,
    isLoading: true,
    error: null,
    updateError: null,
  });
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');

  const fetchProfile = useCallback(async (): Promise<void> => {
    setState((s) => ({ ...s, isLoading: true, error: null }));
    try {
      const profile = await profileApi.getProfile();
      setState({ profile, isLoading: false, error: null, updateError: null });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load profile';
      setState((s) => ({ ...s, isLoading: false, error: message }));
    }
  }, []);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const handleEditName = useCallback((): void => {
    setNameDraft(state.profile?.displayName ?? '');
    setEditingName(true);
  }, [state.profile?.displayName]);

  const handleSaveName = useCallback(async (): Promise<void> => {
    const trimmed = nameDraft.trim();
    if (!trimmed || trimmed === state.profile?.displayName) {
      setEditingName(false);
      return;
    }
    setState((s) => ({ ...s, updateError: null }));
    try {
      const updated = await profileApi.updateProfile({ displayName: trimmed });
      setState((s) => ({ ...s, profile: updated, updateError: null }));
      setEditingName(false);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to update display name';
      setState((s) => ({ ...s, updateError: message }));
    }
  }, [nameDraft, state.profile?.displayName]);

  const handleCancelEdit = useCallback((): void => {
    setEditingName(false);
  }, []);

  // Loading
  if (state.isLoading && !state.profile) {
    return (
      <main className="min-h-screen p-6" role="main" aria-busy="true">
        <div className="mx-auto max-w-4xl">
          <div className="flex items-center justify-center py-12" role="status" aria-label="Loading profile">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" aria-hidden="true" />
            <span className="ml-3 text-muted-foreground">Loading profile…</span>
          </div>
        </div>
      </main>
    );
  }

  // Error with no data
  if (state.error && !state.profile) {
    return (
      <main className="min-h-screen p-6" role="main">
        <div className="mx-auto max-w-4xl">
          <Alert variant="destructive">
            <AlertDescription>{state.error}</AlertDescription>
          </Alert>
          <Button className="mt-4" onClick={fetchProfile}>
            Retry
          </Button>
        </div>
      </main>
    );
  }

  const { profile } = state;
  if (!profile) return <></>;

  return (
    <main className="min-h-screen p-6" role="main">
      <div className="mx-auto max-w-4xl space-y-6">
        <h1 className="text-3xl font-bold tracking-tight" tabIndex={-1}>
          Profile Settings
        </h1>

        {/* Update error */}
        {state.updateError && (
          <Alert variant="destructive">
            <AlertDescription>{state.updateError}</AlertDescription>
          </Alert>
        )}

        {/* Account Information */}
        <Card>
          <CardHeader>
            <CardTitle id="account-info-heading">Account Information</CardTitle>
          </CardHeader>
          <CardContent>
            <section aria-labelledby="account-info-heading" className="space-y-4">
              <div className="flex items-start gap-4">
                <UserAvatar displayName={profile.displayName} avatarUrl={profile.avatarUrl} />
                <div className="flex-1 space-y-3">
                  {/* Display Name */}
                  <div className="space-y-1">
                    <Label htmlFor="profile-display-name">Display name</Label>
                    {editingName ? (
                      <div className="flex items-center gap-2">
                        <Input
                          id="profile-display-name"
                          className="max-w-sm"
                          type="text"
                          value={nameDraft}
                          onChange={(e) => setNameDraft(e.target.value)}
                          aria-label="Display name"
                          autoFocus
                        />
                        <Button size="sm" onClick={handleSaveName}>
                          Save
                        </Button>
                        <Button size="sm" variant="ghost" onClick={handleCancelEdit}>
                          Cancel
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span id="profile-display-name" className="text-sm font-medium">
                          {profile.displayName}
                        </span>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={handleEditName}
                          aria-label="Edit display name"
                        >
                          Edit
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Email (read-only) */}
                  <div className="space-y-1">
                    <Label htmlFor="profile-email">Email</Label>
                    <p id="profile-email" className="text-sm text-muted-foreground">
                      {profile.email}
                    </p>
                  </div>
                </div>
              </div>
            </section>
          </CardContent>
        </Card>

        {/* My Cards */}
        <Card>
          <CardHeader>
            <CardTitle id="my-cards-heading">My Cards</CardTitle>
          </CardHeader>
          <CardContent>
            <MyCardsSection />
          </CardContent>
        </Card>

        {/* MCP API Keys */}
        <Card>
          <CardHeader>
            <CardTitle id="mcp-keys-heading">AI Assistant Keys</CardTitle>
          </CardHeader>
          <CardContent>
            <McpKeysSection />
          </CardContent>
        </Card>

        {/* Watches */}
        <Card>
          <CardHeader>
            <CardTitle id="watches-heading">Watching</CardTitle>
          </CardHeader>
          <CardContent>
            <MyWatchesSection />
          </CardContent>
        </Card>

        {/* Notification Preferences */}
        <Card>
          <CardHeader>
            <CardTitle id="notification-prefs-heading">Notification Preferences</CardTitle>
          </CardHeader>
          <CardContent>
            <NotificationPreferencesSection />
          </CardContent>
        </Card>
      </div>
    </main>
  );
}
